import type {
  IntegrationConnection,
  IntegrationContractResult,
  IntegrationResource,
} from "@common/api/brandGroupService";

export type ProjectAssignmentDiff = {
  added: string[];
  removed: string[];
};

export const getProjectAssignmentDiff = (
  currentProjectIds: readonly string[],
  nextProjectIds: readonly string[],
): ProjectAssignmentDiff => {
  const current = new Set(currentProjectIds);
  const next = new Set(nextProjectIds);

  return {
    added: [...next].filter((projectId) => !current.has(projectId)),
    removed: [...current].filter((projectId) => !next.has(projectId)),
  };
};

export type ManageableProjectAssignmentChangesInput = {
  currentProjectIds: readonly string[];
  nextProjectIds: readonly string[];
  currentAccessLevels?: Readonly<Record<string, string | null | undefined>>;
  nextAccessLevels?: Readonly<Record<string, string | null | undefined>>;
  manageableProjectIds: readonly string[];
  currentDefaultAccessLevel?: string;
  nextDefaultAccessLevel?: string;
};

export type ManageableProjectAssignmentChanges = ProjectAssignmentDiff & {
  changed: string[];
};

export const getManageableProjectAssignmentChanges = ({
  currentProjectIds,
  nextProjectIds,
  currentAccessLevels = {},
  nextAccessLevels = {},
  manageableProjectIds,
  currentDefaultAccessLevel = "READ",
  nextDefaultAccessLevel = "READ",
}: ManageableProjectAssignmentChangesInput): ManageableProjectAssignmentChanges => {
  const manageable = new Set(manageableProjectIds);
  const current = [...new Set(currentProjectIds)].filter((projectId) => manageable.has(projectId));
  const next = [...new Set(nextProjectIds)].filter((projectId) => manageable.has(projectId));
  const { added, removed } = getProjectAssignmentDiff(current, next);
  const currentSet = new Set(current);
  const changed = next.filter((projectId) =>
    currentSet.has(projectId)
    && (currentAccessLevels[projectId] || currentDefaultAccessLevel)
      !== (nextAccessLevels[projectId] || nextDefaultAccessLevel),
  );

  return { added, removed, changed };
};

export const getNewBulkAssignmentAccessLevel = ({
  isSecondaryResource,
  canDelegatePositiveAccess,
}: {
  isSecondaryResource: boolean;
  canDelegatePositiveAccess: boolean;
}): "READ" | "WRITE" =>
  isSecondaryResource && canDelegatePositiveAccess ? "WRITE" : "READ";

const normalizeConnectionKey = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * The workspace-integrations API exposes mapping identities while the Project
 * API exposes the numeric connection id. Resolve the latter from the
 * refreshed Project contract before persisting a Project assignment.
 */
export const resolveConnectionIdsForKeys = (
  integrations: IntegrationConnection[],
  connectionKeys: readonly string[],
): number[] => {
  const keys = new Set(
    connectionKeys.map(normalizeConnectionKey).filter(Boolean),
  );

  return [
    ...new Set(
      integrations
        .filter((integration) =>
          [
            integration.connectionId,
            integration.slugId,
            integration.externalAccountId,
          ].some((value) => keys.has(normalizeConnectionKey(value))),
        )
        .map((integration) => integration.connectionId)
        .filter((connectionId): connectionId is number =>
          Number.isFinite(connectionId),
        ),
    ),
  ];
};

export type ProjectAssignmentPlan = {
  connectionIds: number[];
  resources: IntegrationResource[];
};

/**
 * Resolves a newly connected workspace mapping into the canonical Project
 * contract. A top-level connection is always retained, even when its provider
 * has no selected child resources.
 */
export const resolveProjectAssignmentPlan = (
  contract: Pick<IntegrationContractResult, "integrations" | "resources">,
  connectionKeys: readonly string[],
  selectedResourceKeys?: readonly string[],
): ProjectAssignmentPlan => {
  const connectionIds = resolveConnectionIdsForKeys(
    contract.integrations,
    connectionKeys,
  );
  const connectionIdSet = new Set(connectionIds);
  const selectedResourceKeySet = selectedResourceKeys == null
    ? null
    : new Set(selectedResourceKeys.map(normalizeConnectionKey).filter(Boolean));

  return {
    connectionIds,
    resources: contract.resources.filter((resource) =>
      resource.connectionId != null &&
      connectionIdSet.has(resource.connectionId) &&
      (selectedResourceKeySet == null || selectedResourceKeySet.has(normalizeConnectionKey(resource.resourceKey))),
    ),
  };
};

export type ProjectAssignmentPlanRetryOptions = {
  attempts?: number;
  wait?: () => Promise<void>;
  requireResources?: boolean;
};

/**
 * OAuth persistence can complete before the integration contract has indexed
 * the new connection. Retry the read boundary, never the eventual writes.
 */
export const resolveProjectAssignmentPlanWithRetry = async (
  loadContract: () => Promise<Pick<IntegrationContractResult, "integrations" | "resources">>,
  connectionKeys: readonly string[],
  selectedResourceKeys?: readonly string[],
  {
    attempts = 3,
    wait = () => Promise.resolve(),
    requireResources = selectedResourceKeys != null,
  }: ProjectAssignmentPlanRetryOptions = {},
): Promise<ProjectAssignmentPlan> => {
  const retryCount = Math.max(1, attempts);
  let lastPlan: ProjectAssignmentPlan = { connectionIds: [], resources: [] };

  for (let attempt = 0; attempt < retryCount; attempt += 1) {
    lastPlan = resolveProjectAssignmentPlan(
      await loadContract(),
      connectionKeys,
      selectedResourceKeys,
    );
    if (lastPlan.connectionIds.length > 0 && (!requireResources || lastPlan.resources.length > 0)) {
      return lastPlan;
    }
    if (attempt + 1 < retryCount) {
      await wait();
    }
  }

  throw new Error("connection_integration_not_ready");
};

/**
 * A connection-management row can contain several workspace identities. Do
 * not let an ambiguous identity assign the first contract match by ordering.
 */
export const resolveCanonicalProjectConnectionIdWithRetry = async (
  loadContract: () => Promise<Pick<IntegrationContractResult, "integrations" | "resources">>,
  connectionKeys: readonly string[],
  options?: ProjectAssignmentPlanRetryOptions,
): Promise<number> => {
  const { connectionIds } = await resolveProjectAssignmentPlanWithRetry(
    loadContract,
    connectionKeys,
    undefined,
    options,
  );
  if (connectionIds.length !== 1) {
    throw new Error("project_integration_connection_id_ambiguous");
  }
  return connectionIds[0];
};
